import { InfoTooltip } from "@impero-com/common/components/infoTooltip/tooltip";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    BlockPresenceTransition,
    FormLabel,
    useBelowBreakpoint,
} from "@impero-com/ui";
import { Typography } from "antd";
import { cx } from "class-variance-authority";
import moment from "moment-timezone";
import { Control, useWatch } from "react-hook-form";

import { Month } from "api/generated/model";

import { useControlActivitiesEstimationQuery } from "../queries/useControlActivitiesEstimationQuery";
import { TestingControlFormSchema } from "../testingControlSchema";

import * as headerTheme from "./BulkControlsTable.css";
import * as theme from "./ControlToTestSection.css";
import { TextSkeleton } from "./TextSkeleton";

const { Text } = Typography;

interface EstimatedActivitiesSectionProps {
    control: Control<TestingControlFormSchema>;
    disableInTransition: boolean;
}

export function EstimatedActivitiesSection({
    control,
    disableInTransition,
}: EstimatedActivitiesSectionProps) {
    const controlId = useWatch({ control, name: "controlId" });
    const period = useWatch({ control, name: "period" });
    const samplingTagIds = useWatch({ control, name: "samplingTagIds" });
    const dueDate = useWatch({ control, name: "dueDate" });

    const hasPeriod = !!(period?.start && period?.end);
    const hasTags = samplingTagIds.length > 0;
    const periodStart = period?.start
        ? {
              month: moment(period.start).locale("en").format("MMMM") as Month,
              year: moment(period.start).year(),
          }
        : { month: "January" as Month, year: 2025 };

    const periodEnd = period?.end
        ? {
              month: moment(period.end).locale("en").format("MMMM") as Month,
              year: moment(period.end).year(),
          }
        : { month: "December" as Month, year: 2026 };

    const hasDueDate = !!dueDate;
    const testingControlDueDate = moment(dueDate).format("YYYY-MM-DD");

    const { data: estimationData, isLoading } =
        useControlActivitiesEstimationQuery(
            {
                controlIds: controlId ? [controlId] : [],
                periodStart,
                periodEnd,
                tagIds: samplingTagIds,
                testingControlDueDate,
            },
            hasPeriod && hasTags && hasDueDate
        );

    const totalCount =
        estimationData?.counts?.[0]?.totalCount?.toString() ?? "0";

    return (
        <BlockPresenceTransition
            disableInitial={disableInTransition}
            isVisible={!!controlId && hasPeriod && hasTags && hasDueDate}
        >
            <EstimatedActivitiesSectionContent
                totalCount={totalCount}
                isLoading={isLoading}
            />
        </BlockPresenceTransition>
    );
}

interface EstimatedActivitiesSectionContentProps {
    totalCount: string;
    isLoading: boolean;
}

function EstimatedActivitiesSectionContent({
    totalCount,
    isLoading,
}: EstimatedActivitiesSectionContentProps) {
    const belowXl = useBelowBreakpoint("xl");
    const { t } = useTranslation();

    return (
        <div className={cx(theme.root, belowXl && theme.rootMobile)}>
            <div className={theme.controlToTestContent}>
                <div className={headerTheme.headerContainer}>
                    <FormLabel>
                        {t(
                            "TestingControlView_EstimatedActivities_Single_Text"
                        )}
                    </FormLabel>
                    <InfoTooltip
                        text={t("TestingControlView_EstimatedActivities_Info")}
                        iconClassName={headerTheme.headerInfoIcon}
                    />
                </div>
                {isLoading ? (
                    <TextSkeleton />
                ) : (
                    <Text>
                        {t(
                            "TestingControlView_EstimatedActivities_Single_Count",
                            { estimatedCount: totalCount }
                        )}
                    </Text>
                )}
            </div>
        </div>
    );
}
